import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLoginDrawer } from '@/contexts/LoginDrawerContext';

import mabarService, {
  EventDetailResponse,
  RoleAvailability,
  Participant,
  SparringTeam,
  JoinMabarPayload,
  isEventPast,
} from '@/services/mabarService';
import { getClientProfile, updateClientProfile, DataProfile } from '@/services/clientProfile';
import { MABAR_FACILITIES_OPTIONS } from '@/services/communityService';
import { MabarDetailSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';
import { ReportOverflowButton } from '@/components/common/ReportOverflowButton';
import { getSportIcon, hasHasilPage } from '@/types/sports';
import { CustomFormAnswerModal } from '@/components/community/CustomFormAnswerModal';
import type { CustomFormAnswerItem } from '@/services/communityService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';

// Helper function to format price
const formatPrice = (price?: number): string => {
  if (price === undefined || price === null || price === 0) return 'Gratis';
  return `Rp. ${price.toLocaleString('id-ID')}`;
};

// Helper function to format date
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Helper function to format time
const formatTime = (startTime: string, endTime: string): string => {
  return formatLocalEventTimeRange(startTime, endTime);
};

// Get initials helper
const getInitials = (name: string): string => {
  if (!name) return 'MB';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Facility icon mapper
const getFacilityIcon = (id: string): string => {
  switch (id) {
    case 'air-minum':
      return 'ph:drop-bold';
    case 'snack':
      return 'ph:cookie-bold';
    case 'p3k':
      return 'ph:first-aid-kit-bold';
    case 'jersey':
      return 'ph:t-shirt-bold';
    case 'rompi':
      return 'ph:vest-bold';
    case 'sarung-tangan':
      return 'ph:hand-eye-bold';
    case 'wasit':
      return 'ph:whistle-bold';
    case 'raket':
      return 'ph:tennis-ball-bold';
    case 'fotografer':
      return 'ph:camera-bold';
    case 'videografer':
      return 'ph:video-camera-bold';
    case 'slowmotion':
      return 'ph:film-strip-bold';
    case 'drone':
      return 'ph:drone-bold';
    default:
      return 'ph:circles-four-bold';
  }
};

// Phone Input Modal (Satu Langkah Lagi)
interface PhoneInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (phone: string) => Promise<void>;
  isLoading: boolean;
}

const PhoneInputModal: React.FC<PhoneInputModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [phoneInput, setPhoneInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPhoneInput('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    let digits = raw.replace(/\D/g, '');
    if (digits.startsWith('62')) {
      digits = digits.slice(2);
    }
    if (digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 13);

    let formatted = digits;
    if (digits.length > 7) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
    }
    setPhoneInput(formatted);
  };

  const rawDigits = phoneInput.replace(/\D/g, '');
  const isValid = rawDigits.startsWith('8') && rawDigits.length >= 9 && rawDigits.length <= 13;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;
    const finalPhone = `0${rawDigits}`;
    await onSubmit(finalPhone);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 sm:p-7 flex flex-col gap-5 shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle bar */}
        <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-2 mb-1 shrink-0 sm:hidden" />

        {/* Close button on modal */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
        >
          <Icon icon="ph:x-bold" className="w-4 h-4" />
        </button>

        {/* Header: Icon + Title & Subtitle */}
        <div className="flex items-center gap-3.5 pr-8">
          <div className="w-12 h-12 rounded-2xl bg-[#FFF5EE] border border-orange-100 flex items-center justify-center text-[#FC480D] shrink-0">
            <Icon icon="ph:device-mobile-bold" className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-[#0E1726]">Satu langkah lagi</h3>
            <p className="text-xs text-gray-400">Cukup sekali, berlaku untuk semua event berikutnya.</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed">
          Tambahkan nomor ponsel aktif untuk bergabung ke event ini. Nomor hanya dilihat host event, bukan pemain lain.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <label htmlFor="phone-input" className="text-xs font-bold text-[#0E1726]">
                Nomor ponsel
              </label>
              <span className="text-[11px] font-semibold text-[#FC480D]">Wajib</span>
            </div>

            <div className="flex items-center rounded-2xl border border-gray-200 focus-within:border-[#FC480D] focus-within:ring-2 focus-within:ring-[#FC480D]/20 bg-white overflow-hidden transition-all">
              <div className="px-4 py-3.5 bg-gray-50/60 border-r border-gray-100 text-sm font-semibold text-gray-700 select-none">
                +62
              </div>
              <input
                id="phone-input"
                type="tel"
                inputMode="numeric"
                value={phoneInput}
                onChange={handlePhoneChange}
                placeholder="812 3456 7890"
                className="flex-1 px-4 py-3.5 text-sm font-semibold text-[#0E1726] placeholder:text-gray-400 focus:outline-hidden bg-transparent"
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-xs ${
              !isValid || isLoading
                ? 'bg-[#D1D5DB] text-white cursor-not-allowed'
                : 'bg-[#FC480D] hover:bg-[#D83B08] text-white shadow-md active:scale-98 cursor-pointer'
            }`}
          >
            {isLoading ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Icon icon="ph:check-bold" className="w-4 h-4" />
                <span>Lengkapi Data</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Join Role & Team Selection Modal
interface JoinRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (role: RoleAvailability, team?: string) => void;
  roles: RoleAvailability[];
  eventName: string;
  isLoading: boolean;
  isSparring?: boolean;
  teams?: SparringTeam[];
  participants?: Participant[];
  playerRoles?: { role: string; slots: number; price: number }[];
  isWaitingList?: boolean;
}

const JoinRoleModal: React.FC<JoinRoleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  roles,
  eventName,
  isLoading,
  isSparring = false,
  teams = [],
  participants = [],
  playerRoles = [],
  isWaitingList = false,
}) => {
  const [step, setStep] = useState<'team' | 'role'>('team');
  const [selectedTeamIndex, setSelectedTeamIndex] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleAvailability | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(isSparring ? 'team' : 'role');
      setSelectedTeamIndex(null);
      setSelectedRole(null);
    }
  }, [isOpen, isSparring]);

  if (!isOpen) return null;

  const activeTeams =
    teams.length >= 2
      ? teams
      : [
          { name: 'Tim A', jersey_color: '#FC480D' },
          { name: 'Tim B', jersey_color: '#3B7DDB' },
        ];

  const getRoleAvailabilityForTeam = (
    role: RoleAvailability,
    chosenTeamName?: string
  ): RoleAvailability => {
    if (!isSparring || !chosenTeamName) return role;

    const configuredSlots =
      playerRoles.find((r) => r.role === role.role)?.slots ?? Math.ceil(role.total / 2);

    let filledForTeam = 0;
    if (role.team_member_counts && chosenTeamName) {
      const keys = Object.keys(role.team_member_counts);
      const directKey = keys.find((k) => k.toLowerCase() === chosenTeamName.toLowerCase());
      if (directKey !== undefined) {
        filledForTeam = role.team_member_counts[directKey];
      }
    } else {
      filledForTeam = participants.filter(
        (p) => p.role === role.role && p.team?.toLowerCase() === chosenTeamName.toLowerCase()
      ).length;
    }

    const availableForTeam = Math.max(0, configuredSlots - filledForTeam);
    return {
      ...role,
      total: configuredSlots,
      filled: filledForTeam,
      available: availableForTeam,
    };
  };

  const handleProceedTeamStep = () => {
    if (selectedTeamIndex === null) return;
    setStep('role');
  };

  const currentTeam = selectedTeamIndex !== null ? activeTeams[selectedTeamIndex] : undefined;

  const displayedRoles = roles.map((r) =>
    getRoleAvailabilityForTeam(r, currentTeam?.name)
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle bar */}
        <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1 shrink-0 sm:hidden" />

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-3 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#0E1726]">Pilih Posisi Mabar</h3>
            <p className="text-xs text-gray-500 line-clamp-1">{eventName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4 overflow-y-auto pr-1 flex-1">
          {/* STEP 1: SELECT TEAM */}
          {isSparring && step === 'team' && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Langkah 1: Pilih Tim Sparring
              </p>
              <div className="grid grid-cols-2 gap-3">
                {activeTeams.map((team, idx) => {
                  const isSelected = selectedTeamIndex === idx;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedTeamIndex(idx)}
                      className={`p-4 rounded-2xl border text-left transition-all relative cursor-pointer ${
                        isSelected
                          ? 'border-[#FC480D] bg-orange-50/40 ring-2 ring-[#FC480D]/20'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0 border border-white shadow-xs"
                          style={{ backgroundColor: team.jersey_color || '#FC480D' }}
                        />
                        <span className="text-sm font-bold text-[#0E1726]">{team.name}</span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium block">
                        Pilih untuk bergabung ke {team.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                disabled={selectedTeamIndex === null}
                onClick={handleProceedTeamStep}
                className="w-full mt-4 py-3 bg-[#FC480D] text-white text-sm font-bold rounded-2xl hover:bg-[#D83B08] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Lanjut Pilih Posisi
              </button>
            </div>
          )}

          {/* STEP 2: SELECT ROLE */}
          {(!isSparring || step === 'role') && (
            <div className="space-y-3">
              {isSparring && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                  <span className="text-xs font-semibold text-gray-600">
                    Tim Terpilih: <strong className="text-[#0E1726]">{currentTeam?.name}</strong>
                  </span>
                  <button
                    onClick={() => setStep('team')}
                    className="text-xs text-[#FC480D] font-bold hover:underline"
                  >
                    Ubah Tim
                  </button>
                </div>
              )}

              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {isSparring ? 'Langkah 2: Pilih Peran / Posisi' : 'Pilih Peran / Posisi Bermain'}
              </p>

              <div className="space-y-2.5">
                {displayedRoles.map((role, index) => {
                  const isSelected = selectedRole?.role === role.role;
                  const isRoleFull = role.available <= 0;
                  const canSelect = !isRoleFull || isWaitingList;

                  return (
                    <div
                      key={index}
                      onClick={() => canSelect && setSelectedRole(role)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between ${
                        !canSelect
                          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                          : isSelected
                          ? 'border-[#FC480D] bg-orange-50/40 ring-2 ring-[#FC480D]/20'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-[#0E1726] capitalize">
                            {role.role.replace('_', ' ')}
                          </span>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              !isRoleFull
                                ? 'bg-emerald-100 text-emerald-700'
                                : isWaitingList
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {!isRoleFull ? `Sisa ${role.available} slot` : isWaitingList ? 'Waiting List' : 'Penuh'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {isRoleFull && isWaitingList ? 'Masuk daftar tunggu' : `Total ${role.total} slot • Terisi ${role.filled}`}
                        </p>
                      </div>

                      <div className="text-right">
                        <span className="text-sm font-bold text-[#FC480D]">
                          {formatPrice(role.price)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                disabled={!selectedRole || isLoading}
                onClick={() => selectedRole && onConfirm(selectedRole, currentTeam?.name)}
                className="w-full mt-4 py-3.5 bg-[#FC480D] text-white text-sm font-bold rounded-2xl hover:bg-[#D83B08] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : selectedRole && selectedRole.available <= 0 && isWaitingList ? (
                  <span>Gabung Waiting List</span>
                ) : (
                  <span>Konfirmasi & Bergabung</span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ClientMabarDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openLoginDrawer } = useLoginDrawer();

  const [event, setEvent] = useState<EventDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<DataProfile | null>(null);
  const [isJoinedThisMabar, setIsJoinedThisMabar] = useState(false);
  const [joining, setJoining] = useState(false);

  // Modals state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCustomFormModal, setShowCustomFormModal] = useState(false);
  const [customFormAnswers, setCustomFormAnswers] = useState<CustomFormAnswerItem[]>([]);
  const [showConfirmJoinAlert, setShowConfirmJoinAlert] = useState(false);
  const [selectedRoleForConfirm, setSelectedRoleForConfirm] = useState<RoleAvailability | null>(
    null
  );
  const [selectedTeamForConfirm, setSelectedTeamForConfirm] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;
      setIsLoading(true);
      setError(null);

      try {
        const eventData = await mabarService.getEventBySlug(slug);

        if (!eventData) {
          setError('Event mabar tidak ditemukan');
          setIsLoading(false);
          return;
        }

        setEvent(eventData);

        if (!isAuthenticated) {
          setUserProfile(null);
          setIsJoinedThisMabar(false);
          return;
        }

        const [profileData, joinedStatus] = await Promise.all([
          getClientProfile().catch(() => null),
          mabarService.checkEventJoined(slug).catch(() => ({ joined: false, status: '' })),
        ]);

        if (profileData?.data) {
          setUserProfile(profileData.data);
        }

        const isParticipantInList =
          eventData.participants &&
          profileData?.data?.name &&
          eventData.participants.some(
            (p) =>
              p.name?.trim().toLowerCase() === profileData.data.name?.trim().toLowerCase()
          );

        const userHasJoined =
          eventData.is_host ||
          (joinedStatus?.joined === true &&
            Boolean(joinedStatus.status) &&
            (joinedStatus.status === 'confirmed' || joinedStatus.status === 'active')) ||
          Boolean(isParticipantInList);

        setIsJoinedThisMabar(userHasJoined);
      } catch (err: any) {
        setError(err.message || 'Gagal memuat detail event mabar');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug, isAuthenticated]);

  const hasHasil = hasHasilPage(
    event?.sport_category?.slug || event?.sport_category?.name,
    event?.config?.is_sparring
  );

  const isPast = isEventPast(event?.event_date, event?.start_time, event?.end_time);

  const maxWaitingList = event?.config?.waiting_list_slots || 6;
  const currentWaitingListCount =
    event?.participants?.filter((p) => p.status === 'waiting_list').length || 0;
  const isWaitingListFull =
    Boolean(event?.config?.waiting_list) && currentWaitingListCount >= maxWaitingList;

  const handleJoinClick = () => {
    if (isJoinedThisMabar || isPast) {
      if (hasHasil && event?.slug) {
        navigate(`/event/${event.slug}/statistik`);
      } else {
        navigate(-1);
      }
      return;
    }
    if (!event) return;
    if (!isAuthenticated) {
      openLoginDrawer();
      return;
    }

    if (event.available_slots <= 0) {
      if (event.config?.waiting_list) {
        if (isWaitingListFull) {
          showToast.error(
            'Maaf, kuota pemain dan batas antrean daftar tunggu (waiting list) untuk event ini sudah penuh'
          );
          return;
        }
        // Slot reguler penuh tetapi fitur waiting list aktif & masih ada kuota antrean:
        // Cek nomor hp pemain sebelum memilih posisi waiting list
        const userPhone = userProfile?.phone?.trim();
        if (!userPhone) {
          setShowPhoneModal(true);
          return;
        }
        setShowJoinModal(true);
        return;
      }

      showToast.error('Maaf, slot pemain untuk event ini sudah penuh');
      return;
    }

    // Cek ketersediaan nomor ponsel pemain
    const userPhone = userProfile?.phone?.trim();
    if (!userPhone) {
      setShowPhoneModal(true);
      return;
    }

    setShowJoinModal(true);
  };

  const handlePhoneSubmit = async (phone: string) => {
    setSavingPhone(true);
    try {
      const resp = await updateClientProfile({ phone });
      if (resp?.success !== false) {
        setUserProfile((prev) =>
          prev ? { ...prev, phone } : ({ phone } as DataProfile)
        );
        showToast.success('Nomor ponsel berhasil disimpan');
        setShowPhoneModal(false);
        setShowJoinModal(true);
      } else {
        showToast.error(resp?.message || 'Gagal menyimpan nomor ponsel');
      }
    } catch (err: any) {
      console.error('Error updating phone:', err);
      showToast.error(
        err.response?.data?.message || err.message || 'Gagal menyimpan nomor ponsel'
      );
    } finally {
      setSavingPhone(false);
    }
  };

  const handleJoinConfirm = (role: RoleAvailability, team?: string) => {
    setSelectedRoleForConfirm(role);
    setSelectedTeamForConfirm(team);
    setShowJoinModal(false);

    if (event?.custom_form && event.custom_form.length > 0) {
      setShowCustomFormModal(true);
    } else if (event?.config?.auto_confirm === false) {
      setShowConfirmJoinAlert(true);
    } else {
      executeJoin(role, team, []);
    }
  };

  const handleCustomFormSubmit = (answers: CustomFormAnswerItem[]) => {
    setCustomFormAnswers(answers);
    setShowCustomFormModal(false);

    // If there's an answer for Team template, sync it to team
    const teamAns = answers.find(
      (a) =>
        (a.title || '').toLowerCase().trim() === 'tim' ||
        (a.title || '').toLowerCase().includes('tim') ||
        (a.question_id && a.question_id.toLowerCase().includes('team')) ||
        (a.field_id && a.field_id.toLowerCase().includes('team'))
    );
    const finalTeam =
      teamAns && typeof teamAns.value === 'string' && teamAns.value.trim()
        ? teamAns.value.trim()
        : selectedTeamForConfirm;

    if (finalTeam) {
      setSelectedTeamForConfirm(finalTeam);
    }

    if (event?.config?.auto_confirm === false) {
      setShowConfirmJoinAlert(true);
    } else {
      executeJoin(selectedRoleForConfirm || undefined, finalTeam, answers);
    }
  };

  const executeJoin = async (
    roleObj?: RoleAvailability,
    teamStr?: string,
    answersList?: CustomFormAnswerItem[]
  ) => {
    const roleToUse = roleObj || selectedRoleForConfirm;
    const teamToUse = teamStr !== undefined ? teamStr : selectedTeamForConfirm;
    const answersToUse = answersList !== undefined ? answersList : customFormAnswers;

    if (!roleToUse || !slug) return;

    setJoining(true);
    try {
      const payload: JoinMabarPayload = {
        email: userProfile?.email,
        role: roleToUse.role,
        team: teamToUse,
        wa_number: userProfile?.phone,
        custom_form_answer: answersToUse && answersToUse.length > 0 ? answersToUse : undefined,
      };

      const res = await mabarService.joinEvent(slug, payload);

      if (res.success) {
        showToast.success(res.message || 'Berhasil bergabung ke event!');
        setShowJoinModal(false);
        setShowCustomFormModal(false);
        setShowConfirmJoinAlert(false);

        if (res.data?.payment_checkout_payload && res.data?.is_awal_qris) {
          navigate(`/event/checkout/${res.data.invoice_number}`, {
            state: {
              paymentInstruction: res.data.payment_checkout_payload,
              invoiceNumber: res.data.invoice_number,
              amount: res.data.price,
              eventName: event?.name,
            },
          });
        } else {
          navigate('/event');
        }
      } else {
        showToast.error(res.message || 'Gagal bergabung ke event');
      }
    } catch (err: any) {
      console.error('Failed to join mabar:', err);
      showToast.error(err.response?.data?.message || err.message || 'Gagal bergabung ke event');
    } finally {
      setJoining(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: event?.name || 'Mabar Sporto',
      text: `Yuk ikutan Mabar ${event?.name || ''} di Sporto!`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // User cancelled share
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast.success('Link event berhasil disalin!');
    }
  };

  if (isLoading) {
    return <MabarDetailSkeleton />;
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-6 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 text-center border border-[#E0E6ED] space-y-4 max-w-md w-full shadow-xs">
          <div className="w-14 h-14 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mx-auto">
            <Icon icon="ph:warning-circle-bold" className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-[#0E1726]">Detail Mabar Tidak Ditemukan</h3>
          <p className="text-xs text-gray-500 leading-relaxed">
            {error || 'Event mabar tidak tersedia atau telah dihapus oleh penyelenggara.'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="w-full py-3 bg-[#0E1726] text-white font-bold text-sm rounded-2xl hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  const isFull = event.available_slots <= 0;
  const lowestPrice =
    event.roles_availability && event.roles_availability.length > 0
      ? Math.min(...event.roles_availability.map((r) => r.price))
      : 0;

  const facilitiesList = event.config?.facilities || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0E1726] pb-24 lg:pb-12">
      {/* DESKTOP BREADCRUMB HEADER */}
      <div className="hidden md:block bg-white border-b border-[#E0E6ED] px-6 py-4 mb-6 shadow-2xs">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              title="Kembali"
            >
              <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#0E1726]">Detail Event Mabar</h1>
              <p className="text-xs text-gray-500">Informasi lengkap, jadwal, dan pendaftaran event mabar</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleShare}
              className="h-10 px-4 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-xs font-semibold shadow-2xs transition-all cursor-pointer"
            >
              <Icon icon="ph:share-network-bold" className="w-4 h-4 text-gray-600" />
              <span>Bagikan</span>
            </button>
            <ReportOverflowButton type="Event" title={event?.name || slug || 'Event'} />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* LEFT / MAIN COLUMN */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-4 md:space-y-5">
            {/* 1. HEADER CARD: Badges & Title & Participant Avatars */}
            <div className="rounded-3xl p-5 md:p-6 space-y-3.5 shadow-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-xs px-2.5 py-0.5 font-bold rounded-lg ${
                    isFull
                      ? event.config?.waiting_list && !isWaitingListFull
                        ? 'bg-amber-50 border border-amber-200 text-amber-700'
                        : 'bg-rose-50 border border-rose-200 text-rose-600'
                      : 'bg-emerald-50 border border-emerald-200 text-emerald-600'
                  }`}
                >
                  {isFull
                    ? event.config?.waiting_list && !isWaitingListFull
                      ? 'Waiting List'
                      : 'Penuh'
                    : 'Available'}
                </span>

                {event.sport_category && (
                  <span className="text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-100 px-2.5 py-0.5 rounded-lg flex items-center gap-1.5">
                    <Icon
                      icon={getSportIcon(event.sport_category.slug || event.sport_category.name)}
                      className="w-4 h-4 text-[#FC480D]"
                    />
                    <span>{event.sport_category.name}</span>
                  </span>
                )}

                {event.config?.is_sparring && (
                  <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-lg flex items-center gap-1">
                    <Icon icon="ph:swords-bold" className="w-3.5 h-3.5 text-blue-600" />
                    <span>Sparring</span>
                  </span>
                )}
              </div>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[#0E1726] leading-snug">
                {event.name}
              </h1>

              {/* Participant avatars stack */}
              <div
                onClick={() => navigate(`/event/${slug}/participants`)}
                className="flex items-center gap-2 cursor-pointer pt-1 hover:opacity-85 transition-opacity"
              >
                <div className="flex -space-x-2 overflow-hidden">
                  {event.participants &&
                    event.participants.slice(0, 5).map((p, idx) => (
                      <div
                        key={idx}
                        className="w-7 h-7 rounded-full bg-[#0E1726] border-2 border-white text-white text-[10px] font-bold flex items-center justify-center overflow-hidden"
                      >
                        {p.avatar ? (
                          <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          getInitials(p.name)
                        )}
                      </div>
                    ))}
                </div>

                <div className="flex items-center gap-1 text-xs font-semibold text-[#0E1726]">
                  <span>
                    {event.filled_slots}/{event.total_slots} Bergabung
                  </span>
                  <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* 2. MOBILE ONLY: MAIN CARD (Price, Facilities, Schedule) */}
            <div className="lg:hidden bg-white rounded-3xl border border-[#E0E6ED] p-5 space-y-4 shadow-xs !mt-0">
              {/* Price Row */}
              <div>
                <p className="text-xs text-gray-400 font-medium">Mulai dari</p>
                <p className="text-2xl font-extrabold text-[#FC480D] mt-0.5">
                  {formatPrice(lowestPrice)}
                </p>
                <p className="text-xs font-semibold text-[#0E1726] mt-1">
                  {isFull
                    ? event.config?.waiting_list
                      ? isWaitingListFull
                        ? 'Slot & Waiting List Penuh!'
                        : `Slot Penuh (Daftar Tunggu: ${currentWaitingListCount}/${maxWaitingList})`
                      : 'Slot Penuh!'
                    : `Tersisa ${event.available_slots} slot lagi!`}
                </p>
              </div>

              {/* Facilities Section (if configured) */}
              {facilitiesList.length > 0 && (
                <div className="border-t border-gray-100 pt-3.5 space-y-2.5">
                  <h4 className="text-sm font-bold text-[#0E1726]">Fasilitas</h4>
                  <div className="flex flex-wrap gap-2">
                    {facilitiesList.map((facilityId) => {
                      const option = MABAR_FACILITIES_OPTIONS.find((f) => f.id === facilityId);
                      const label = option?.label || facilityId;
                      const icon = getFacilityIcon(facilityId);

                      return (
                        <div
                          key={facilityId}
                          className="bg-gray-50 border border-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-1.5"
                        >
                          <Icon icon={icon} className="w-4 h-4 text-[#FC480D]" />
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Date & Time */}
              <div className="border-t border-gray-100 pt-3.5 space-y-2 text-xs font-medium text-gray-600">
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:calendar-blank-bold" className="w-4 h-4 text-gray-400" />
                  <span>{formatDate(event.event_date)}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:clock-bold" className="w-4 h-4 text-gray-400" />
                  <span>{formatTime(event.start_time, event.end_time)}</span>
                </div>
              </div>
            </div>

            {/* 3. DESKTOP ONLY: JADWAL & FASILITAS CARD */}
            <div className="hidden lg:block bg-white rounded-3xl border border-[#E0E6ED] p-6 space-y-5 shadow-xs">
              <h3 className="text-base font-bold text-[#0E1726]">Jadwal & Waktu Event</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3.5 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                    <Icon icon="ph:calendar-blank-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 font-medium">Tanggal</p>
                    <p className="text-sm font-bold text-[#0E1726] mt-0.5">{formatDate(event.event_date)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3.5 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center flex-shrink-0">
                    <Icon icon="ph:clock-bold" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 font-medium">Waktu Main</p>
                    <p className="text-sm font-bold text-[#0E1726] mt-0.5">{formatTime(event.start_time, event.end_time)}</p>
                  </div>
                </div>
              </div>

              {facilitiesList.length > 0 && (
                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <h4 className="text-sm font-bold text-[#0E1726]">Fasilitas Termasuk</h4>
                  <div className="flex flex-wrap gap-2.5">
                    {facilitiesList.map((facilityId) => {
                      const option = MABAR_FACILITIES_OPTIONS.find((f) => f.id === facilityId);
                      const label = option?.label || facilityId;
                      const icon = getFacilityIcon(facilityId);

                      return (
                        <div
                          key={facilityId}
                          className="bg-gray-50 border border-gray-100 text-gray-700 text-xs font-semibold px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-2xs"
                        >
                          <Icon icon={icon} className="w-4 h-4 text-[#FC480D]" />
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. ALAMAT SECTION */}
            <div className="rounded-3xl p-5 md:p-6 space-y-4 shadow-xs !mt-0">
              <h3 className="text-base font-bold text-[#0E1726]">Alamat & Lokasi</h3>
              <div className="flex items-start gap-3 text-xs md:text-sm">
                <Icon icon="mdi:map-marker-outline" className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#0E1726]">{event.lokasi_arena}</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {[event.address, event.city].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>

              {/* Map Preview Banner */}
              {event.map_link ? (
                <button
                  type="button"
                  onClick={() => {
                    const url = event.map_link.startsWith('http')
                      ? event.map_link
                      : `https://${event.map_link}`;
                    void openExternalUrl(url);
                  }}
                  className="w-full h-36 md:h-48 rounded-2xl flex items-center justify-center relative overflow-hidden group shadow-xs border border-gray-200 cursor-pointer"
                  style={{
                    backgroundImage: 'url(/images/dummy-map.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 backdrop-blur-[6px] bg-black/25 group-hover:backdrop-blur-[4px] transition-all" />
                  <div className="text-center relative z-10 flex flex-col items-center gap-1.5">
                    <Icon
                      icon="mdi:map"
                      className="w-10 h-10 text-white drop-shadow-lg group-hover:scale-110 transition-transform"
                    />
                    <span className="px-4 py-1.5 rounded-full bg-white/90 text-[#0E1726] font-bold text-xs shadow-md">
                      Buka di Google Maps
                    </span>
                  </div>
                </button>
              ) : (
                <div
                  className="w-full h-36 md:h-48 rounded-2xl flex items-center justify-center relative overflow-hidden border border-gray-200"
                  style={{
                    backgroundImage: 'url(/images/dummy-map.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 backdrop-blur-[6px] bg-black/20" />
                  <div className="text-center relative z-10">
                    <Icon
                      icon="mdi:map"
                      className="w-10 h-10 text-white/70 mx-auto mb-1 drop-shadow-lg"
                    />
                    <p className="text-white/80 font-semibold text-xs drop-shadow-lg">Peta Tidak Tersedia</p>
                  </div>
                </div>
              )}
            </div>

            {/* 5. DESKRIPSI SECTION */}
            {event.description && (
              <div className="rounded-3xl p-5 md:p-6 space-y-3 shadow-xs !mt-0">
                <h3 className="text-base font-bold text-[#0E1726]">Deskripsi Event</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed whitespace-pre-line font-medium">
                  {event.description}
                </p>
              </div>
            )}

            {/* 6. SYARAT & KETENTUAN SECTION */}
            {event.tnc && (
              <div className="rounded-3xl p-5 md:p-6 space-y-3 shadow-xs !mt-0">
                <h3 className="text-base font-bold text-[#0E1726]">Syarat & Ketentuan</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed whitespace-pre-line font-medium">
                  {event.tnc}
                </p>
              </div>
            )}

            {/* 7. PENYELENGGARA SECTION */}
            <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 md:p-6 space-y-3 shadow-xs">
              <h3 className="text-base font-bold text-[#0E1726]">Penyelenggara</h3>
              <div
                onClick={() =>
                  event.host?.slug && navigate(`/community/${event.host.slug}`)
                }
                className="p-4 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-between cursor-pointer hover:border-gray-300 hover:bg-white transition-all shadow-2xs"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-[#0E1726] flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-xs">
                    {event.host?.avatar ? (
                      <img src={event.host.avatar} alt={event.host.name} className="w-full h-full object-cover" />
                    ) : (
                      getInitials(event.host?.name || 'Garuda')
                    )}
                  </div>
                  <div>
                    <p className="text-sm md:text-base font-bold text-[#0E1726]">{event.host?.name || 'Garuda Fun Community'}</p>
                    <p className="text-xs text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                      <Icon icon="ph:users-three-bold" className="w-3.5 h-3.5 text-gray-400" />
                      <span>Komunitas Penyelenggara</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs font-semibold text-[#FC480D]">
                  <span className="hidden sm:inline">Kunjungi Komunitas</span>
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* 8. PEMAIN TERDAFTAR CARD */}
            <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 md:p-6 space-y-3 shadow-xs">
              <div
                onClick={() => navigate(`/event/${slug}/participants`)}
                className="flex items-center justify-between cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center text-[#FC480D] flex-shrink-0">
                    <Icon icon="ph:users-bold" className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#0E1726]">Pemain Terdaftar</h3>
                    <p className="text-xs text-gray-400 font-medium mt-0.5">
                      {event.filled_slots}/{event.total_slots} slot telah terisi
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {event.participants &&
                      event.participants.slice(0, 4).map((p, i) => (
                        <div
                          key={i}
                          className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white overflow-hidden text-[10px] font-bold flex items-center justify-center text-gray-700"
                        >
                          {p.avatar ? (
                            <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            getInitials(p.name)
                          )}
                        </div>
                      ))}
                  </div>
                  <span className="text-xs font-bold text-[#FC480D] hidden sm:inline ml-1">
                    Lihat Semua
                  </span>
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT / SIDEBAR COLUMN (DESKTOP STICKY JOIN CARD) */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-4 sticky top-6 space-y-4">
            <div className="bg-white rounded-3xl border border-[#E0E6ED] p-6 space-y-5 shadow-xs">
              {/* Price & Slot availability header */}
              <div>
                <p className="text-xs text-gray-400 font-medium">Mulai dari</p>
                <div className="flex items-baseline justify-between gap-2 mt-0.5">
                  <p className="text-3xl font-extrabold text-[#FC480D]">
                    {formatPrice(lowestPrice)}
                  </p>
                  <span
                    className={`text-xs px-2.5 py-1 font-bold rounded-lg ${
                      isFull
                        ? event.config?.waiting_list && !isWaitingListFull
                          ? 'bg-amber-50 border border-amber-200 text-amber-700'
                          : 'bg-rose-50 border border-rose-200 text-rose-600'
                        : 'bg-emerald-50 border border-emerald-200 text-emerald-600'
                    }`}
                  >
                    {isFull
                      ? event.config?.waiting_list && !isWaitingListFull
                        ? 'Waiting List'
                        : 'Slot Penuh'
                      : `Tersisa ${event.available_slots} Slot`}
                  </span>
                </div>
                <p className="text-xs font-semibold text-[#0E1726] mt-2">
                  {isFull
                    ? event.config?.waiting_list
                      ? isWaitingListFull
                        ? 'Slot & Waiting List Penuh!'
                        : `Slot Penuh (Daftar Tunggu: ${currentWaitingListCount}/${maxWaitingList})`
                      : 'Slot Penuh!'
                    : `Tersisa ${event.available_slots} slot lagi dari total ${event.total_slots} slot`}
                </p>

                {/* Occupancy Progress Bar */}
                <div className="w-full bg-gray-100 h-2 rounded-full mt-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isFull ? 'bg-rose-500' : 'bg-[#FC480D]'
                    }`}
                    style={{
                      width: `${Math.min(100, Math.round((event.filled_slots / (event.total_slots || 1)) * 100))}%`,
                    }}
                  />
                </div>
              </div>

              {/* Positions / Roles list */}
              {event.roles_availability && event.roles_availability.length > 0 && (
                <div className="border-t border-gray-100 pt-4 space-y-2.5">
                  <div className="flex items-center justify-between text-xs font-bold text-[#0E1726]">
                    <span>Posisi & Tarif</span>
                    <span className="text-gray-400 font-normal">Ketersediaan</span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {event.roles_availability.map((r, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs"
                      >
                        <div>
                          <span className="font-bold text-[#0E1726] capitalize">
                            {r.role.replace('_', ' ')}
                          </span>
                          <span className="text-[11px] text-gray-500 block">
                            {formatPrice(r.price)}
                          </span>
                        </div>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            r.available > 0
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {r.available > 0 ? `Sisa ${r.available}` : 'Penuh'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <button
                type="button"
                onClick={handleJoinClick}
                disabled={
                  isFull &&
                  (!event?.config?.waiting_list || isWaitingListFull) &&
                  !isJoinedThisMabar &&
                  !isPast
                }
                className={`w-full h-12 font-bold text-sm rounded-full transition-all active:scale-98 flex items-center justify-center gap-2 cursor-pointer shadow-md ${
                  isJoinedThisMabar || isPast
                    ? 'bg-[#0E1726] hover:bg-[#1C2838] text-white'
                    : isFull && (!event?.config?.waiting_list || isWaitingListFull)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                    : 'bg-[#FC480D] hover:bg-[#D83B08] text-white shadow-orange-500/20'
                }`}
              >
                {isJoinedThisMabar || isPast ? (
                  <>
                    <Icon icon="ph:chart-bar-bold" className="w-5 h-5" />
                    <span>Lihat Statistik Event</span>
                  </>
                ) : isFull ? (
                  event?.config?.waiting_list ? (
                    isWaitingListFull ? (
                      'Waiting List Penuh'
                    ) : (
                      'Gabung Waiting List'
                    )
                  ) : (
                    'Slot Penuh'
                  )
                ) : (
                  'Bergabung Sekarang'
                )}
              </button>

              {/* Guarantee badges */}
              <div className="space-y-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500">
                <div className="flex items-center gap-2">
                  <Icon icon="ph:shield-check-bold" className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Konfirmasi instan & transaksi aman terproteksi</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon icon="ph:arrows-clockwise-bold" className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>Pembatalan sesuai kebijakan penyelenggara</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE ONLY: STICKY BOTTOM ACTION BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white/95 backdrop-blur-md border-t border-gray-100 p-3.5 px-4 z-40 flex items-center gap-3 shadow-lg">
        <button
          onClick={handleShare}
          className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 active:scale-95 transition-all flex-shrink-0 cursor-pointer"
        >
          <Icon icon="ph:share-network-bold" className="w-5 h-5" />
        </button>
        <ReportOverflowButton type="Event" title={event?.name || slug || 'Event'} />

        <button
          onClick={handleJoinClick}
          disabled={
            isFull &&
            (!event?.config?.waiting_list || isWaitingListFull) &&
            !isJoinedThisMabar &&
            !isPast
          }
          className={`flex-1 h-12 font-bold text-sm rounded-full transition-all active:scale-98 flex items-center justify-center gap-2 cursor-pointer ${
            isJoinedThisMabar || isPast
              ? 'bg-[#0E1726] hover:bg-[#1C2838] text-white shadow-md'
              : isFull && (!event?.config?.waiting_list || isWaitingListFull)
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              : 'bg-[#FC480D] hover:bg-[#D83B08] text-white shadow-md'
          }`}
        >
          {isJoinedThisMabar || isPast ? (
            <>
              <Icon icon="ph:chart-bar-bold" className="w-5 h-5" />
              <span>Lihat Statistik</span>
            </>
          ) : isFull ? (
            event?.config?.waiting_list ? (
              isWaitingListFull ? (
                'Waiting List Penuh'
              ) : (
                'Gabung Waiting List'
              )
            ) : (
              'Slot Penuh'
            )
          ) : (
            'Bergabung Sekarang'
          )}
        </button>
      </div>

      {/* PHONE INPUT MODAL (SEBELUM PILIH POSISI) */}
      <PhoneInputModal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSubmit={handlePhoneSubmit}
        isLoading={savingPhone}
      />

      {/* JOIN ROLE SELECTION MODAL */}
      <JoinRoleModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onConfirm={handleJoinConfirm}
        roles={event.roles_availability || []}
        eventName={event.name}
        isLoading={joining}
        isSparring={event.config?.is_sparring}
        teams={event.config?.teams}
        participants={event.participants}
        playerRoles={event.config?.player_roles}
        isWaitingList={Boolean(event.config?.waiting_list && !isWaitingListFull)}
      />

      {/* CUSTOM FORM ANSWER MODAL (DATA TAMBAHAN DARI HOST) */}
      {event.custom_form && event.custom_form.length > 0 && (
        <CustomFormAnswerModal
          isOpen={showCustomFormModal}
          onClose={() => setShowCustomFormModal(false)}
          customForm={event.custom_form}
          onSubmit={handleCustomFormSubmit}
          isLoading={joining}
          rolePrefill={selectedRoleForConfirm?.role}
        />
      )}

      {/* CONFIRM JOIN ALERT DIALOG */}
      <AlertDialog open={showConfirmJoinAlert} onOpenChange={setShowConfirmJoinAlert}>
        <AlertDialogContent className="max-w-md rounded-3xl p-6">
          <AlertDialogHeader>
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 text-[#FC480D]">
              <Icon icon="ph:user-plus-bold" className="w-7 h-7" />
            </div>
            <AlertDialogTitle className="text-center text-lg font-bold text-[#0E1726]">
              {selectedRoleForConfirm && selectedRoleForConfirm.available <= 0 && event?.config?.waiting_list
                ? 'Gabung Waiting List Event'
                : 'Bergabung dengan Event'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-xs text-gray-500 leading-relaxed mt-1">
              {selectedRoleForConfirm && selectedRoleForConfirm.available <= 0 && event?.config?.waiting_list ? (
                <>
                  Apakah Anda yakin ingin bergabung ke daftar tunggu (waiting list) event{' '}
                  <strong>{event.name}</strong> dengan posisi{' '}
                  <strong className="capitalize">
                    {selectedRoleForConfirm?.role.replace('_', ' ')}
                  </strong>
                  ?
                  <br />
                  <br />
                  Anda akan otomatis dipromosikan menjadi peserta jika ada pemain aktif yang membatalkan keikutsertaan.
                </>
              ) : (
                <>
                  Apakah Anda yakin ingin bergabung dengan event <strong>{event.name}</strong> dengan
                  peran{' '}
                  <strong className="capitalize">
                    {selectedRoleForConfirm?.role.replace('_', ' ')}
                  </strong>
                  ?
                  <br />
                  <br />
                  Bergabung dengan event ini <strong>membutuhkan konfirmasi dari host event.</strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter className="flex gap-2 sm:gap-2 mt-4">
            <AlertDialogCancel
              disabled={joining}
              className="flex-1 rounded-2xl py-3 border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => executeJoin()}
              disabled={joining}
              className="flex-1 rounded-2xl py-3 bg-[#FC480D] hover:bg-[#D83B08] text-white font-bold"
            >
              {joining
                ? 'Memproses...'
                : selectedRoleForConfirm && selectedRoleForConfirm.available <= 0 && event?.config?.waiting_list
                ? 'Ya, Gabung Waiting List'
                : 'Ya, Bergabung'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientMabarDetailPage;
